import { atom } from "jotai";

/**
 * Terminal reason a geocoding batch stopped being tracked (BUG-410).
 *
 * `"completed"` — the backend reported `pending === 0`; every address reached a
 * terminal state (resolved or definitively failed).
 *
 * `"timeout"` — `pending` stopped moving for the poller's stall window. This is
 * the BUG-415 defence: `geocode_order` has no retry policy, so a worker restart
 * or a raising provider leaves an order with `lat/lon = NULL` AND
 * `geocode_failed = false` — permanently "pending". A design that waits for
 * `pending === 0` would spin forever, so the banner terminates itself and names
 * the unresolved remainder instead of lying about progress.
 */
export type GeocodeProgressOutcome = "completed" | "timeout";

/** Shape of an in-progress (or just-finished) post-import geocoding batch. */
export interface GeocodeProgressTask {
  /** `import_batch_id` from the import response — the polling key. */
  batchId: string;
  /**
   * How many of the imported orders were queued for geocoding, as reported by
   * the import itself. The denominator until the first poll lands.
   */
  total: number;
  /** Latest counts from the progress endpoint; null until the first poll. */
  pending: number | null;
  done: number | null;
  failed: number | null;
  /**
   * Set once the batch stops being polled. While null the banner shows live
   * progress; once set it shows a terminal state and the poller is disabled.
   */
  outcome: GeocodeProgressOutcome | null;
}

/**
 * Null when no post-import geocoding is being tracked.
 * Seeded right after `POST /v1/orders/import` returns `geocoding_total > 0`
 * (a total of 0 means nothing needs geocoding → no banner at all).
 */
export const geocodeProgressAtom = atom<GeocodeProgressTask | null>(null);
